import React, { useContext } from "react";
import { useLocation } from "react-router-dom";
import { Grid, Box, Typography, Container } from "@mui/material";
import { TraduccionesContext } from "components/shared/TraduccionesContext";
import ContactoCover from "../../../assets/images/Contacto.png";
import IconMail from "../../../assets/images/icon-mail.svg";
import IconPhone from "../../../assets/images/icon-phone.svg";
import HeaderFrontOffice from "../../../components/FrontOfficeHeaders/HeaderFrontOffice";
import { FrontOfficeFooter } from "../../../components/FrontOfficeFooter";
import { ContactForm } from "../../../components/ContactForm";
import SeoRoutesContext from "../../../components/shared/SeoRoutesContext";
import { Helmet } from "react-helmet";

/** Color primario de la sección de contacto (alineado con diseño Athloan). */
const CONTACT_SECTION_PRIMARY = "#4b59a7";
/** Color de las líneas separadoras cortas (no a ancho completo). */
const CONTACT_STRIP_DIVIDER_COLOR = "#e5e5ea";

/**
 * Textos de la franja "Contáctanos" según diseño (ES) y equivalente para EN.
 * Se usa aquí para que coincidan con el mockup; el formulario y el pie siguen usando traducciones API.
 */
const CONTACT_STRIP_COPY = {
  es: {
    badge: "CONTÁCTANOS",
    title: "Estamos Aquí Para Ayudarte",
    subtitle: "Contacta con nosotros con cualquier duda que tengas.",
    phoneLabel: "Teléfono",
    phoneDisplay: "93 215 35 01 / 911 861 137",
    phoneTel: "tel:+34932153501",
    emailLabel: "Correo Electrónico",
    email: "info@althloan.com",
    whatsappLabel: "Whatsapp",
    whatsappDisplay: "+34 644 71 23 46",
    whatsappWaMe: "34644712346",
    officesTitle: "Nuestras Oficinas",
    officesSubtitle: "Te atenderemos personalmente en Barcelona y Madrid",
  },
  en: {
    badge: "CONTACT US",
    title: "We're Here to Help",
    subtitle: "Contact us with any questions you may have.",
    phoneLabel: "Phone",
    phoneDisplay: "+34 93 215 35 01 / +34 911 861 137",
    phoneTel: "tel:+34932153501",
    emailLabel: "Email",
    email: "info@althloan.com",
    whatsappLabel: "WhatsApp",
    whatsappDisplay: "+34 644 71 23 46",
    whatsappWaMe: "34644712346",
    officesTitle: "Our Offices",
    officesSubtitle: "We will assist you in person in Barcelona and Madrid",
  },
};

function ContactosPage() {
  const { traducciones } = useContext(TraduccionesContext);
  const seoRoutes = useContext(SeoRoutesContext);
  const { pathname } = useLocation();
  const stripLang = /^\/en(\/|$)/i.test(pathname) ? "en" : "es";
  const strip = CONTACT_STRIP_COPY[stripLang];
  const contactBadgeText = traducciones?.CONTACTO?.contact_badge || strip.badge;

  return (
    <>
      <Helmet>
        <link rel="canonical" href={`${document.location.href}`} />
        {seoRoutes
          .filter((x) => x.SEO_PAGE_ID === "8")
          .map(
            (
              lang,
              index // `index` es el índice del elemento actual en el array
            ) => (
              <link
                key={lang.ACRONIMO_ISO + index} // Concatenación del ISO y el índice para asegurar unicidad
                rel="alternate"
                hrefLang={lang.ACRONIMO_ISO.toLowerCase()}
                href={`${document.location.origin}/${lang.ACRONIMO_ISO.toLowerCase()}/${
                  lang.SEO_URL_FRIENDLY.endsWith("/")
                    ? lang.SEO_URL_FRIENDLY
                    : lang.SEO_URL_FRIENDLY + "/"
                }`}
              />
            )
          )}
      </Helmet>
      <Box>
        <HeaderFrontOffice keySelected="contacto" />
      </Box>
      <Box
        sx={{ background: "#FFF", display: "flex", justifyContent: "center", marginTop: "64px" }}
      >
        <Container
          sx={{
            paddingTop: "3rem",
            maxWidth: {
              xl: "1320px!important", // max-width 1320px para xl
              lg: "1140px!important", // max-width 1140px para lg
              md: "100%!important", // max-width 100% para md
              sm: "100%!important", // max-width 100% para sm
              xs: "100%!important", // max-width 100% para xs
            },
            "& *": { fontFamily: "Nunito !important" },
          }}
        >
          <Grid container sx={{ marginBottom: "2rem" }}>
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              lg={7}
              xl={7}
              padding={2}
              paddingLeft={0}
              display={"flex"}
              alignContent={"center"}
              flexWrap={"wrap"}
              justifyContent={{
                xs: "center",
                lg: "flex-end",
                xl: "flex-end",
              }}
              sx={{
                "@media (min-width:991px) and (max-width:1100px)": {
                  justifyContent: "flex-start", // Esto hace que se oculte a partir de 920px
                },
              }}
            >
              <Box
                component="img"
                src={ContactoCover}
                alt="Contacto"
                style={{
                  maxWidth: "666px",
                  maxHeight: "500px",
                  aspectRatio: "666/500",
                  textAlign: "center",
                }}
                sx={{
                  width: {
                    xs: "100%", // max-width 100% para xs
                    sm: "100%", // max-width 100% para sm
                    md: "100%", // max-width 100% para md
                    lg: "100%", // max-width 1140px para lg
                    xl: "100%", // max-width 1320px para xl
                  },

                  "@media (min-width:1400px)": {
                    width: "auto", // Esto hace que se oculte a partir de 920px
                  },
                  "@media (min-width:991px) and (max-width:1100px)": {
                    width: "80%", // Esto hace que se oculte a partir de 920px
                  },
                }}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              lg={5}
              xl={5}
              padding={2}
              paddingLeft={"0"}
              display={"flex"}
              sx={{
                justifyContent: {
                  xs: "center",
                  lg: "flex-end!important", // max-width 1140px para lg
                },
              }}
            >
              <Box
                sx={{
                  display: {
                    xs: "block", // max-width 100% para xs
                    lg: "flex", // max-width 1140px para lg
                  },

                  maxWidth: {
                    xs: "100%", // max-width 100% para xs
                    lg: "408px", // max-width 1140px para lg
                  },

                  flexDirection: "column",
                }}
              >
                <Box>
                  <Typography
                    gutterBottom
                    fontSize={"40px"}
                    sx={{
                      lineHeight: "1",
                      color: "#222222",
                      fontWeight: "bold",
                      marginBottom: 0,
                      paddingTop: "5px",
                    }}
                  >
                    {traducciones && traducciones.CONTACTO.form_title}
                  </Typography>
                  <Typography fontSize={"16px"}>
                    {traducciones && traducciones.CONTACTO.form_subtitle}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "start",
                    alignItems: "center",
                    marginTop: "25px",
                  }}
                >
                  <Box sx={{ width: "408px" }}>
                    <ContactForm rightAligned={true} />
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box
        component="section"
        aria-labelledby="contact-section-heading"
        sx={{
          width: "100%",
          backgroundColor: "#fff",
          /* Sin margen superior: evita que se vea el fondo gris del body entre el formulario y esta sección. */
          mt: 0,
          "& *": { fontFamily: "Nunito !important" },
        }}
      >
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            width: "100%",
            backgroundColor: "#fff",
            /* Menos padding arriba: acerca la 1.ª línea al bloque "Explícanos tu caso". El aire grande va entre línea ↔ contenido de "Estamos Aquí...". */
            pt: { xs: 1, md: 1.5 },
            pb: { xs: 4, md: 6 },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              borderRadius: "50%",
              backgroundColor: "rgba(75, 89, 167, 0.06)",
              width: 220,
              height: 220,
              left: -90,
              top: "18%",
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              borderRadius: "50%",
              backgroundColor: "rgba(75, 89, 167, 0.05)",
              width: 160,
              height: 160,
              right: -40,
              top: "8%",
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              borderRadius: "50%",
              backgroundColor: "rgba(75, 89, 167, 0.04)",
              width: 280,
              height: 280,
              right: -100,
              bottom: "5%",
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              borderRadius: "50%",
              backgroundColor: "rgba(75, 89, 167, 0.035)",
              width: 120,
              height: 120,
              right: "12%",
              top: "35%",
              pointerEvents: "none",
            }}
          />

          <Container
            sx={{
              position: "relative",
              zIndex: 1,
              maxWidth: {
                xl: "1320px!important",
                lg: "1140px!important",
                md: "100%!important",
                sm: "100%!important",
                xs: "100%!important",
              },
            }}
          >
            <Box
              role="presentation"
              sx={{
                /* "1px": en sx, height: 1 se transforma en 100% (sizingTransform de MUI) y colapsa sin altura útil. */
                height: "1px",
                flexShrink: 0,
                bgcolor: CONTACT_STRIP_DIVIDER_COLOR,
                width: { xs: "98%", sm: "96%", md: "min(1200px, 94%)" },
                maxWidth: 1200,
                mx: "auto",
                mt: 0,
                mb: { xs: 5.5, md: 7 },
              }}
            />
            <Box sx={{ textAlign: { xs: "center", md: "left" } }}>
              <Typography
                component="span"
                sx={{
                  display: "inline-block",
                  bgcolor: CONTACT_SECTION_PRIMARY,
                  color: "#fff",
                  fontSize: "11px",
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  px: 1.75,
                  py: 0.75,
                  borderRadius: "9999px",
                  textTransform: "uppercase",
                  mb: 2,
                }}
              >
                {contactBadgeText}
              </Typography>
              <Typography
                id="contact-section-heading"
                component="h2"
                sx={{
                  fontSize: { xs: "28px", md: "40px" },
                  lineHeight: 1.1,
                  color: "#222222",
                  fontWeight: 700,
                  mb: 1.5,
                }}
              >
                {strip.title}
              </Typography>
              <Typography
                sx={{
                  fontSize: "16px",
                  color: "#6C6B70",
                  maxWidth: 520,
                  mx: { xs: "auto", md: 0 },
                  mb: { xs: 3, md: 4 },
                }}
              >
                {strip.subtitle}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2.5,
                alignItems: { xs: "center", md: "flex-start" },
                maxWidth: 480,
                mx: { xs: "auto", md: 0 },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: "10px",
                    bgcolor: CONTACT_SECTION_PRIMARY,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Box
                    component="img"
                    src={IconPhone}
                    alt=""
                    sx={{ width: 24, height: 24, filter: "brightness(0) invert(1)" }}
                  />
                </Box>
                <Box sx={{ textAlign: "left", minWidth: 0 }}>
                  <Typography
                    sx={{ fontSize: "15px", fontWeight: 600, color: "#5a5a5f", mb: 0.25 }}
                  >
                    {strip.phoneLabel}
                  </Typography>
                  <Typography
                    component="a"
                    href={strip.phoneTel}
                    sx={{
                      fontSize: "17px",
                      fontWeight: 600,
                      color: CONTACT_SECTION_PRIMARY,
                      textDecoration: "none",
                      wordBreak: "break-word",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {strip.phoneDisplay}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: "10px",
                    bgcolor: CONTACT_SECTION_PRIMARY,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Box
                    component="img"
                    src={IconMail}
                    alt=""
                    sx={{ width: 24, height: 24, filter: "brightness(0) invert(1)" }}
                  />
                </Box>
                <Box sx={{ textAlign: "left", minWidth: 0 }}>
                  <Typography
                    sx={{ fontSize: "15px", fontWeight: 600, color: "#5a5a5f", mb: 0.25 }}
                  >
                    {strip.emailLabel}
                  </Typography>
                  <Typography
                    component="a"
                    href={`mailto:${strip.email}`}
                    sx={{
                      fontSize: "17px",
                      fontWeight: 600,
                      color: CONTACT_SECTION_PRIMARY,
                      textDecoration: "none",
                      wordBreak: "break-all",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {strip.email}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: "10px",
                    bgcolor: CONTACT_SECTION_PRIMARY,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <svg fill="none" viewBox="0 0 360 362" width="26" height="26" aria-hidden>
                    <path
                      fill="#ffffff"
                      fillRule="evenodd"
                      d="M307.546 52.566C273.709 18.684 228.706.017 180.756 0 81.951 0 1.538 80.404 1.504 179.235c-.017 31.594 8.242 62.432 23.928 89.609L0 361.736l95.024-24.925c26.179 14.285 55.659 21.805 85.655 21.814h.077c98.788 0 179.21-80.413 179.244-179.244.017-47.898-18.608-92.926-52.454-126.807v-.008Zm-126.79 275.788h-.06c-26.73-.008-52.952-7.194-75.831-20.765l-5.44-3.231-56.391 14.791 15.05-54.981-3.542-5.638c-14.912-23.721-22.793-51.139-22.776-79.286.035-82.14 66.867-148.973 149.051-148.973 39.793.017 77.198 15.53 105.328 43.695 28.131 28.157 43.61 65.596 43.593 105.398-.035 82.149-66.867 148.982-148.982 148.982v.008Zm81.719-111.577c-4.478-2.243-26.497-13.073-30.606-14.568-4.108-1.496-7.09-2.243-10.073 2.243-2.982 4.487-11.568 14.577-14.181 17.559-2.613 2.991-5.226 3.361-9.704 1.117-4.477-2.243-18.908-6.97-36.02-22.226-13.313-11.878-22.304-26.54-24.916-31.027-2.613-4.486-.275-6.91 1.959-9.136 2.011-2.011 4.478-5.234 6.721-7.847 2.244-2.613 2.983-4.486 4.478-7.469 1.496-2.991.748-5.603-.369-7.847-1.118-2.243-10.073-24.289-13.812-33.253-3.636-8.732-7.331-7.546-10.073-7.692-2.613-.13-5.595-.155-8.586-.155-2.991 0-7.839 1.118-11.947 5.604-4.108 4.486-15.677 15.324-15.677 37.361s16.047 43.344 18.29 46.335c2.243 2.991 31.585 48.225 76.51 67.632 10.684 4.615 19.029 7.374 25.535 9.437 10.727 3.412 20.49 2.931 28.208 1.779 8.604-1.289 26.498-10.838 30.228-21.298 3.73-10.46 3.73-19.433 2.613-21.298-1.117-1.865-4.108-2.991-8.586-5.234l.008-.017Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </Box>
                <Box sx={{ textAlign: "left", minWidth: 0 }}>
                  <Typography
                    sx={{ fontSize: "15px", fontWeight: 600, color: "#5a5a5f", mb: 0.25 }}
                  >
                    {strip.whatsappLabel}
                  </Typography>
                  <Typography
                    component="a"
                    href={`https://wa.me/${strip.whatsappWaMe}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      fontSize: "17px",
                      fontWeight: 600,
                      color: CONTACT_SECTION_PRIMARY,
                      textDecoration: "none",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {strip.whatsappDisplay}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box
              role="presentation"
              sx={{
                height: "1px",
                flexShrink: 0,
                bgcolor: CONTACT_STRIP_DIVIDER_COLOR,
                width: { xs: "98%", sm: "96%", md: "min(1200px, 94%)" },
                maxWidth: 1200,
                mx: "auto",
                mt: { xs: 5.5, md: 7 },
                mb: { xs: 1, md: 1.5 },
              }}
            />
          </Container>
        </Box>

        <Container
          sx={{
            position: "relative",
            zIndex: 1,
            maxWidth: {
              xl: "1320px!important",
              lg: "1140px!important",
              md: "100%!important",
              sm: "100%!important",
              xs: "100%!important",
            },
            pt: { xs: 3, md: 4 },
            pb: { xs: 1, md: 2 },
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <Typography
              component="h3"
              sx={{
                fontSize: { xs: "28px", md: "36px" },
                lineHeight: 1.1,
                color: "#222222",
                fontWeight: 700,
                mb: 1,
              }}
            >
              {strip.officesTitle}
            </Typography>
            <Typography sx={{ fontSize: "16px", color: "#6C6B70" }}>
              {strip.officesSubtitle}
            </Typography>
          </Box>
        </Container>
      </Box>

      <Grid
        container
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: "#FFF",
          paddingTop: "48px",
        }}
      >
        <Grid
          item
          xs={12}
          sm={12}
          md={6}
          lg={6}
          xl={6}
          padding={2}
          paddingLeft={{
            xs: "16px",
            md: "0px",
          }}
        >
          <iframe
            src={traducciones && traducciones.CONTACTO.address_two_iframe}
            width="600"
            height="450"
            style={{ border: 0, width: "100%" }}
            allowfullscreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></iframe>
        </Grid>
        <Grid
          item
          xs={12}
          sm={12}
          md={6}
          lg={6}
          xl={6}
          padding={2}
          paddingRight={{
            xs: "16px",
            md: "0px",
          }}
        >
          <iframe
            src={traducciones && traducciones.CONTACTO.address_one_iframe}
            width="600"
            height="450"
            style={{ border: 0, width: "100%" }}
            allowfullscreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></iframe>
        </Grid>
      </Grid>

      <FrontOfficeFooter />
    </>
  );
}
export default ContactosPage;
